import { createBrowserRouter, type RouteObject } from 'react-router-dom';
import type { ComponentType } from 'react';
import RootProviders from './RootProviders';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AppLayout from './components/layout/AppLayout';
import RoleGate from './components/auth/RoleGate';
import ErrorBoundary from './components/common/ErrorBoundary';
import NotFoundPage from './components/common/NotFoundPage';
import RouteErrorFallback from './components/common/RouteErrorFallback';
import DashboardPage from './components/dashboard/DashboardPage';
import { importWithChunkRetry } from './utils/lazyWithRetry';

function lazyPage(
  importPage: () => Promise<{ default: ComponentType }>,
  errorTitle: string,
): RouteObject['lazy'] {
  return async () => {
    const { default: Page } = await importWithChunkRetry(importPage);
    return {
      Component: function LazyPage() {
        return (
          <ErrorBoundary fallbackTitle={errorTitle}>
            <Page />
          </ErrorBoundary>
        );
      },
    };
  };
}

export const router = createBrowserRouter([
  {
    element: <RootProviders />,
    errorElement: <RouteErrorFallback />,
    children: [
      {
        path: '/login',
        errorElement: <RouteErrorFallback />,
        lazy: lazyPage(
          () => import('./components/auth/LoginPage'),
          'Login error',
        ),
      },
      {
        element: <ProtectedRoute />,
        children: [
          {
            element: <AppLayout />,
            children: [
              {
                index: true,
                errorElement: <RouteErrorFallback />,
                element: (
                  <ErrorBoundary fallbackTitle="Dashboard error">
                    <DashboardPage />
                  </ErrorBoundary>
                ),
              },
              // DISABLED: Current Expense route (see docs/DISABLED_FEATURES.md Feature #11)
              // {
              //   path: 'expense/*',
              //   errorElement: <RouteErrorFallback />,
              //   lazy: lazyPage(
              //     () => import('./components/current-expense/CurrentExpensePage'),
              //     'Current Expense error',
              //   ),
              // },
              {
                path: 'planning/*',
                errorElement: <RouteErrorFallback />,
                lazy: async () => {
                  const { default: PlanningRoute } = await importWithChunkRetry(
                    () => import('./routes/PlanningRoute'),
                  );
                  return { Component: PlanningRoute };
                },
              },
              {
                path: 'help/:topicId?',
                errorElement: <RouteErrorFallback />,
                lazy: lazyPage(
                  () => import('./components/help/HelpPage'),
                  'Help Center error',
                ),
              },
              {
                path: 'admin/*',
                errorElement: <RouteErrorFallback />,
                lazy: async () => {
                  const { default: AdminPage } = await importWithChunkRetry(
                    () => import('./components/admin/AdminPage'),
                  );
                  return {
                    Component: function AdminRoute() {
                      return (
                        <RoleGate adminOrAnyOf={['feature.imports']}>
                          <ErrorBoundary fallbackTitle="Admin error">
                            <AdminPage />
                          </ErrorBoundary>
                        </RoleGate>
                      );
                    },
                  };
                },
              },
              {
                path: '*',
                element: <NotFoundPage />,
              },
            ],
          },
        ],
      },
    ],
  },
], {
  // Render the destination route immediately so its own loading UI shows
  // instead of keeping the previous page visible during the transition.
  future: {
    v7_startTransition: false,
  },
});
